import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { BehaviorSubject, filter, flatMap, map, merge, Observable, of, switchMap, tap, catchError } from 'rxjs';
import { IEndOfYearTotals, IExpensesFixedBasicComponents, IGrossOperatingIncome, IGrossScheduledIncome, IGrossScheduledIncomeComponents, IInvestmentVariables, IInvestmentYear, ILoan, ILoanParams, ILoanPayment, IOperatingExpenses, IPropertyEntity, IPropertyInfo, IPropertyNewData, PropertyService } from 'src/app/services/poperty.service';
import { Location } from '@angular/common';
import { TabsetComponent } from 'ng-uikit-pro-standard';
import { StringUtil } from 'src/app/lib/string-util';
import { DateUtil } from 'src/app/lib/date-util';
import { IFormViewModel } from 'src/app/forms/forms';
import { FormInputComponent, IFormInputViewModel } from 'src/app/forms/form-input/form-input.component';
import { FormOkCancelComponent, IFormOkCancelViewModel } from 'src/app/forms/form-ok-cancel/form-ok-cancel.component';
import { IPageHeaderViewModel } from 'src/app/components/page-header/page-header.component';
import { IPageLoaderViewModel } from 'src/app/components/page-loader/page-loader.component';
import { FormCheckboxComponent, IFormCheckboxViewModel } from 'src/app/forms/form-checkbox/form-checkbox.component';
import { IBadgeViewModel } from 'src/app/components/badge/badge.component';
import { FormFileEntryListComponent, IFormFileEntryListViewModel } from 'src/app/forms/form-file-entry-list/form-file-entry-list.component';
import { NumberUtil } from 'src/app/lib/number-util';

@Component({
  selector: 'app-property-page',
  templateUrl: './property-page.component.html',
  styleUrls: ['./property-page.component.scss']
})
export class PropertyPageComponent implements OnInit {

  pageVm$: Observable<IPageViewModel>;

  /**
   * 
   * @param propertyService 
   * @param activatedRoute 
   */
  constructor(private propertyService: PropertyService, private activatedRoute: ActivatedRoute,
    private location: Location, private router: Router
  ) { }

  /**
   * 
   */
  @ViewChild('tabset', { static: false }) tabset: TabsetComponent;

  private refresher$ = new BehaviorSubject<{ propertyCode: string, tab: number }>(undefined);

  //#region help text / html

  /**
   * 
   * @returns 
   */
  private titleText_PurchaseCosts() {
    return 'Purchase Costs';
  }

  /**
   * 
   * @returns 
   */
  private helpText_PurchaseCosts() {
    return 'Anything beyond typical closing costs... i.e. HOA, home warranty, mortgage prepayment, etc.';
  }

  /**
   * 
   * @returns 
   */
  private helpText_ClosingCosts() {
    return `Costs excluding prepaid taxes and insurance, usually ${StringUtil.toPercent(this.closingCostsPercent() * .01, { fractionDigits: 1})} of the purchase price`;
  }

  /**
   * 
   * @param passiveAmount 
   * @param taxRate 
   * @returns 
   */
  private helpHtml_PassiveDepreciation(passiveAmount: number, taxRate: number): string {
    const ret: string = `Offsets passive income<br/>${StringUtil.toCurrency(passiveAmount ?? 0)} (cashflow) x ${StringUtil.toPercent(taxRate, { fractionDigits: 2 })} (taxrate)`;
    return ret;
  }

  /**
   * 
   * @param activeAmount 
   * @param taxRate 
   */
  private helpHtml_ActiveDepreciation(activeAmount: number, taxRate: number): string {
    const ret: string = `Offsets active income if REPS<br/>${StringUtil.toCurrency(activeAmount ?? 0)} (remainder after cashflow) x ${StringUtil.toPercent(taxRate, { fractionDigits: 2 })} (taxrate)`;
    return ret;
  }

  /**
   * 
   * @returns 
   */
  private closingCostsPercent(): number {
    return 1.5;
  }

  //#endregion 

  //#region generic form stuff

  /**
   * 
   * @param pvm 
   * 
   */
  formCancel(pvm: IPageViewModel) {
    // refresh types
    const refreshTypes = [ 'property-new', 'property-copy' ];
    
    if (refreshTypes.find(rt => rt == pvm.contentVm.formVm.type)) {      
      this.router.navigate(['/']);
    }

    pvm.contentVm.formVm = undefined;
    pvm.pageHeaderVm.formDisplayed = false;

  }

  /**
   * 
   * @param pvm 
   * @param fvm 
   */
  formComplete(pvm: IPageViewModel, fvm: IFormViewModel) {
    if (fvm.type == 'property-edit')
      this.propertyEditFormComplete(pvm, <IPropertyEditFormViewModel>fvm);
    if (fvm.type == 'property-new')
      this.propertyNewFormComplete(pvm, <IPropertyNewFormViewModel>fvm);
    if (fvm.type == 'property-delete')
      this.propertyDeleteFormComplete(pvm, <IPropertyDeleteFormViewModel>fvm);
    if (fvm.type == 'property-copy')
      this.propertyCopyFormComplete(pvm, <IPropertyCopyFormViewModel>fvm);
    if (fvm.type == 'loan-view')
      this.formCancel(pvm);
    if (fvm.type == 'disclaimer')
      this.formCancel(pvm);
  }

  //#endregion generic form stuff

  /**
   * 
   * @param pvm 
   */
  backClicked(pvm: IPageViewModel) {
    this.location.back();
  }

  /**
   * 
   * @param pvm 
   */
  editClicked(pvm: IPageViewModel) {
    this.propertyEditFormStart(pvm.contentVm);
    pvm.pageHeaderVm.formDisplayed = true;
  }

  /**
   * 
   * @param pvm 
   */
  deleteClicked(pvm: IPageViewModel) {
    this.propertyDeleteFormStart(pvm.contentVm);
    pvm.pageHeaderVm.formDisplayed = true;
  }

  /**
   * 
   * @param pvm 
   */
  infoClicked(pvm: IPageViewModel) {
    this.disclaimerFormStart(pvm.contentVm);
    pvm.pageHeaderVm.formDisplayed = true;
  }

  /**
   * 
   * @param pvm 
   */
  copyClicked(pvm: IPageViewModel) {
    this.propertyCopyFormStart(pvm.contentVm);
    pvm.pageHeaderVm.formDisplayed = true;
  }

  /**
   * 
   */
  ngOnInit(): void {
    const watcher$ = merge(
      this.activatedRoute.paramMap.pipe(
        map((pm: ParamMap) => ({
          propertyCode: pm.get('property-code'),
          tab: 1
        }))
      ),
      this.refresher$
    ).pipe(
      filter(ctx => ctx != undefined),
      switchMap(ctx => {

        const pf$ = this.propertyService.propertyFind(ctx.propertyCode).pipe(
          map(property => ({ ...ctx, property: property }))
        ).pipe(
          map(ctx => {        
            // edit new property, or show existing
            const propertyNewFormVm = ctx.propertyCode == 'new-property' ? this.propertyNewFormVm(this.propertyService.propertyNewData()) : undefined;

            // page view model
            const ret: IPageViewModel = {
              pageHeaderVm: {
                title: propertyNewFormVm ? 'Add Property' : ctx.property ? ctx.property.propertyInfo.name : 'Not Found',
                edit: propertyNewFormVm ? false : true, delete: propertyNewFormVm ? false : true, info: propertyNewFormVm ? false : true, copy: propertyNewFormVm ? false : true
              },
              contentVm: {
                isNew: propertyNewFormVm ? true : false,
                data: ctx.property,

                chartVms: !ctx.property ? [] : [
                  PropertyPageComponent.propertyCashflowChartVm('Cashflow', [ctx.property], 35),
                  PropertyPageComponent.propertyEquityChartVm('Equity', [ctx.property], 35)
                ],

                formVm: propertyNewFormVm ?? undefined,

                infoVm: !ctx.property ? undefined : this.infoVm(ctx.property),
                investmentYearsVm: !ctx.property ? undefined : this.investmentYearsVm(ctx.property)
              }
            };
            return ret;

          })
        );

        return merge(pf$, of({ workingText: 'Loading...' }));
      }),
      catchError(err => {
        const errVm: IPageViewModel = {
          errorText: err
        };
        return of(errVm);
      })
    );

    this.pageVm$ = watcher$;
  }

  /**
   * 
   * @param property 
   * @returns 
   */
  private infoVm(property: IPropertyEntity): IPropertyInfoViewModel {
    const yearStart = DateUtil.now().getFullYear();
    const yearEnd = yearStart + 5;

    // general panel
    const generalPanelVm: IPagePanelViewModel = {
      title: 'General',
      badgeVm: !property.inactive ? undefined : { type: 'danger', title: 'Inactive ' },
      sections: [
        {
          title: undefined,
          items: [
            { title: 'Address', value: property.propertyInfo.address },
            { title: 'Description', value: property.propertyInfo.description }
          ]
        },
        {
          title: '',
          items: [
            { title: 'Purchase Price', value: StringUtil.toCurrency(property.propertyInfo.purchasePrice) },
            { title: 'Purchase Date', value: property.propertyInfo.purchaseDate },
            {
              title: this.titleText_PurchaseCosts(),
              value: StringUtil.toCurrency((property.propertyInfo.purchaseCosts ?? 0.0)),
              helpHtml: this.helpText_PurchaseCosts()
            }
          ]
        }
      ]
    };
    
    const proFormaPanelVm: IPagePanelViewModel = {
      title: 'Pro Forma',
      helpHtml: 'Projected year 1 numbers',
      sections: [
        {
          title: undefined,
          items: [
            { title: 'Market Value', value: StringUtil.toCurrency(property.propertyInfo.initialValues.marketValue) },
            { title: 'Initial Investment', value: StringUtil.toCurrency(property.initialInvestment) }
          ]
        },
        {
          title: '',
          items: [
            { title: 'Monthly Rent', value: StringUtil.toCurrency(property.propertyInfo.initialValues.monthlyRevenue) },
            { title: 'Yearly Revenue', value: StringUtil.toCurrency(property.propertyInfo.initialValues.monthlyRevenue * 12) }
          ]
        },
        {
          title: '',
          items: [
            {
              title: 'Grounds',
              value: StringUtil.toCurrency((property.investmentYears[0].operatingExpenses.estimated.fixed.grounds ?? 0) / 12),
              helpHtml: 'Monthly Grounds'
            },
            {
              title: 'HOA',
              value: StringUtil.toCurrency((property.investmentYears[0].operatingExpenses.estimated.fixed.hoa ?? 0) / 12),
              helpHtml: 'Monthly HOA'
            },
            {
              title: 'Utilities',
              value: StringUtil.toCurrency((property.investmentYears[0].operatingExpenses.estimated.fixed.utilities ?? 0) / 12),
              helpHtml: 'Monthly utilities'
            },
            {
              title: 'Property Mgmt.',
              value: StringUtil.toCurrency((property.investmentYears[0].operatingExpenses.estimated.fixed.propertyManagement ?? 0) / 12),
              helpHtml: 'Monthly property management'
            },
            {
              title: 'Insurance',
              value: StringUtil.toCurrency((property.investmentYears[0].operatingExpenses.estimated.fixed.insurance ?? 0)),
              helpHtml: 'Annual insurance'
            },
            {
              title: 'Taxes',
              value: StringUtil.toCurrency((property.investmentYears[0].operatingExpenses.estimated.fixed.taxes ?? 0)),
              helpHtml: 'Annual taxes'
            },
            {
              title: 'Other',
              value: StringUtil.toCurrency((property.investmentYears[0].operatingExpenses.estimated.fixed.other ?? 0)),
              helpHtml: 'Annual other expenses'
            },
            {
              title: 'Loan',
              value: StringUtil.toCurrency((property.loan?.monthlyPayment ?? 0) * 12),
              helpHtml: 'Annual principal and interest'
            },
          ].filter(i => i.value != '$ 0')
        },
        {
          title: 'ROI',
          items: [
            {
              title: 'Cashflow',
              value: `${StringUtil.toCurrency((property.investmentYears[0].endOfYearTotals.estimated.cashFlow ?? 0))} (${StringUtil.toPercent((property.investmentYears[0].cashOnCash.cashflow ?? 0), { fractionDigits: 2 })})`
            },
            {
              title: 'Appreciation',
              value: `${StringUtil.toCurrency((property.investmentYears[0].endOfYearTotals.estimated.appreciation ?? 0))} (${StringUtil.toPercent((property.investmentYears[0].cashOnCash.appreciation ?? 0), { fractionDigits: 2 })})`
            },
            {
              title: 'Paydown',
              value: `${StringUtil.toCurrency((property.investmentYears[0].endOfYearTotals.estimated.tenantPaydown ?? 0))} (${StringUtil.toPercent((property.investmentYears[0].cashOnCash.tenantPaydown ?? 0), { fractionDigits: 2 })})`
            },
            {
              title: 'Total',
              value: `${StringUtil.toCurrency((property.investmentYears[0].endOfYearTotals.estimated.total ?? 0))} (${StringUtil.toPercent((property.investmentYears[0].cashOnCash.total ?? 0), { fractionDigits: 2 })})`
            }
          ]
        },
        {
          title: 'Depreciation',
          items: [
            {
              title: 'Passive Depr.',
              value: `${StringUtil.toCurrency((property.investmentYears[0].endOfYearTotals.estimated.depreciation.passiveTaxImpact ?? 0))} (${StringUtil.toPercent((property.investmentYears[0].cashOnCash.depreciation.passive ?? 0), { fractionDigits: 2 })})`,
              helpHtml: this.helpHtml_PassiveDepreciation(property.investmentYears[0].endOfYearTotals.estimated.depreciation.passive, NumberUtil.percent(property.investmentVariables.taxBracket))
            },
            {
              title: 'Active Depr.',
              value: `${StringUtil.toCurrency((property.investmentYears[0].endOfYearTotals.estimated.depreciation.activeTaxImpact ?? 0))} (${StringUtil.toPercent((property.investmentYears[0].cashOnCash.depreciation.active ?? 0), { fractionDigits: 2 })})`,
              helpHtml: this.helpHtml_ActiveDepreciation(property.investmentYears[0].endOfYearTotals.estimated.depreciation.active, NumberUtil.percent(property.investmentVariables.taxBracket))
            },
            {
              title: 'Total',
              value: StringUtil.toCurrency((property.investmentYears[0].endOfYearTotals.estimated.depreciation.passiveTaxImpact ?? 0) + (property.investmentYears[0].endOfYearTotals.estimated.depreciation.activeTaxImpact ?? 0))
                + ' ('
                + StringUtil.toPercent((property.investmentYears[0].cashOnCash.depreciation.passive ?? 0) + (property.investmentYears[0].cashOnCash.depreciation.active ?? 0), { fractionDigits: 2 })
                + ')',
            }
          ]
        }
      ]
    };

    // loan panel
    const loanPanelVm: IPagePanelViewModel = !property.loan ? undefined : {
      title: 'Loan',      
      actions: [
        { code: 'loan-view', title: 'Payments', tooltip: 'View amortization table' }
      ],
      sections: [
        {
          title: undefined,
          items: [
            { title: 'Interest Rate', value: StringUtil.toPercent(NumberUtil.percent(property.loan.loanParams.rate), { fractionDigits: 2 }) },
            { title: 'Term', value: `${property.loan.loanParams.years} year(s)` },
            { title: 'Downpayment', value: StringUtil.toCurrency(property.loan.downpayment) },
            { title: 'Loan Amount', value: StringUtil.toCurrency(property.loan.amount) },
            { title: 'Closing Costs', value: StringUtil.toCurrency(property.loan.loanParams.closingCosts), helpHtml: this.helpText_ClosingCosts() },
            { title: 'Monthly Payment', value: StringUtil.toCurrency(property.loan.monthlyPayment) }
          ]
        }
      ]
    };

    // info VM
    const ret: IPropertyInfoViewModel = {
      pictureUrl: this.propertyService.imageUrlPrimary(property),
      panelVms: [generalPanelVm, loanPanelVm, proFormaPanelVm].filter(s => s)
    };
    return ret;
  }

  /**
   * 
   * @param vm 
   * @param code 
   */
  infoPanelActionStart(pvm: IPageViewModel, code: string) {
    if (code == 'loan-view') {
      this.loanFormStart(pvm.contentVm);
      pvm.pageHeaderVm.formDisplayed = true;
    }
  }

  /**
   * 
   * @param vm 
   */
  private loanFormStart(vm: IContentViewModel) {
    const frmVm: IPropertyLoanFormViewModel = {
      title: 'Amortization Table',
      type: 'loan-view',

      monthlyPayment: StringUtil.toCurrency(vm.data.loan.monthlyPayment),

      payments: vm.data.loan.payments.filter(p => p.interest > 0).map((p, ndx) => ({
        title: `${p.monthNumber}${(ndx + 1) % 12 == 1 ? ` - Year ${p.yearNumber}` : ''}`,
        start: StringUtil.toCurrency(ndx == 0 ? vm.data.loan.amount : vm.data.loan.payments[ndx - 1].amountRemaining, { fractionDigits: 2, round: false }),
        interest: StringUtil.toCurrency(p.interest, { fractionDigits: 2, round: false }),
        principal: StringUtil.toCurrency(p.principal, { fractionDigits: 2, round: false }),
        end: StringUtil.toCurrency(p.amountRemaining, { fractionDigits: 2, round: false }),
      })),

      okCancelVm: {
        ...FormOkCancelComponent.okCancelVm({ cancelText: '' }),
        isValid: true
      }
    };
    vm.formVm = frmVm;
  }

  /**
   * 
   * @param property 
   * @returns 
   */
  private investmentYearsVm(property: IPropertyEntity): IInvestmentYearsViewModel {    
    const ret: IInvestmentYearsViewModel = {
      title: 'Details',

      tableVm: {
        headers: [
          'Year', 'End of Year', 'Cumulative', !property.loan ? undefined : 'Amortized Loan'
        ].filter(h => h),
        rows: property.investmentYears.map((iy, ndx) => {
          const
            endOfYearLoanDefault = { principal: 0, interest: 0, balance: 0, total: 0 };

          const
            gsi: IGrossScheduledIncome = (iy.grossScheduledIncome.actual ?? iy.grossScheduledIncome.estimated),
            goi: IGrossOperatingIncome = (iy.grossOperatingIncome.actual ?? iy.grossOperatingIncome.estimated),
            expensesFixed: number = (iy.operatingExpenses.actual ?? iy.operatingExpenses.estimated).fixed.total,
            expensesVariable: number = (iy.operatingExpenses.actual ?? iy.operatingExpenses.estimated).variable.total,
            endOfYearTotals: IEndOfYearTotals = iy.endOfYearTotals.actual ?? iy.endOfYearTotals.estimated,
            endOfYearLoan = !property.loan ? endOfYearLoanDefault : property.loan.payments.filter(p => p.yearNumber == iy.year && p.interest > 0).reduce((pv, cv) => {
              return {
                principal: pv.principal + cv.principal,
                interest: pv.interest + cv.interest,
                balance: cv.amountRemaining,
                total: pv.total + (cv.principal + cv.interest)
              }
            }, endOfYearLoanDefault),
            currentYear = iy.calendarYear == DateUtil.now().getFullYear();

          return {
            title: `${iy.calendarYear} - Year ${iy.year}${currentYear ? ' (current)' : ''}`,
            helpHtml: undefined,
            calendarYear: iy.calendarYear,
            highlight: iy.year % 5 == 0 ? true : false,
            actions: [
              // todo... { code: 'view-year', title: 'View' }
            ],
            cells: [
              // end of year
              {
                title: 'End of Year',
                items: [
                  {
                    title: 'Cashflow', value: StringUtil.toCurrency(endOfYearTotals.cashFlow), templateId: 'cashflowTemplate',
                    items: [
                      { title: 'GSI', value: StringUtil.toCurrency(gsi.total) },
                      { title: 'Vacancy', value: `- ${StringUtil.toCurrency(goi.vacancy)}` },
                      { title: 'GOI', value: StringUtil.toCurrency(goi.total) },
                      { title: 'Expenses', value: `- ${StringUtil.toCurrency(expensesFixed + expensesVariable)}` },
                      { title: 'NOI', value: StringUtil.toCurrency(iy.netOperatingIncome) },
                      { title: 'Debt', value: `- ${StringUtil.toCurrency(endOfYearLoan.total)}` },
                      { title: '', value: undefined },
                      { title: 'Cashflow', value: StringUtil.toCurrency(endOfYearTotals.cashFlow) }
                    ]
                  },
                  { title: '', value: undefined },
                  
                  { title: 'Appreciation', value: StringUtil.toCurrency(endOfYearTotals.appreciation) },
                  !property.loan ? undefined : { title: 'Paydown', value: StringUtil.toCurrency(endOfYearTotals.tenantPaydown) },
                  { title: '', value: StringUtil.toCurrency(endOfYearTotals.appreciation + (!property.loan ? 0.0 : endOfYearTotals.tenantPaydown)), isTotal: true },
                  { title: '', value: undefined },
                  
                  {
                    title: 'Passive Depr.',
                    value: StringUtil.toCurrency(endOfYearTotals.depreciation.passiveTaxImpact),
                    helpHtml: this.helpHtml_PassiveDepreciation(endOfYearTotals.depreciation.passive, NumberUtil.percent(property.investmentVariables.taxBracket))
                  },
                  {
                    title: 'Active Depr.',
                    value: StringUtil.toCurrency(endOfYearTotals.depreciation.activeTaxImpact),
                    helpHtml:  this.helpHtml_ActiveDepreciation(endOfYearTotals.depreciation.active, NumberUtil.percent(property.investmentVariables.taxBracket))
                  },
                  {
                    title: '',
                    value: StringUtil.toCurrency(endOfYearTotals.depreciation.passiveTaxImpact + endOfYearTotals.depreciation.activeTaxImpact),
                    isTotal: true
                  }

                ].filter(i => i)
              },

              // cumulative
              {
                title: 'Cumulative',
                items: [
                  { title: 'Cashflow', value: StringUtil.toCurrency(iy.runningTotals.cashflow) },
                  { title: '', value: undefined },
                  
                  { title: 'Appreciation', value: StringUtil.toCurrency(iy.runningTotals.appreciation) },
                  !property.loan ? undefined : { title: 'Paydown', value: StringUtil.toCurrency(iy.runningTotals.tenantPaydown) },    
                  { title: '', value: StringUtil.toCurrency(iy.runningTotals.appreciation + (!property.loan ? 0 : iy.runningTotals.tenantPaydown)), isTotal: true },                  
                  { title: '', value: undefined },

                  { title: 'Passive Depr.', value: StringUtil.toCurrency(iy.runningTotals.depreciation.passiveTaxImpact) },
                  { title: 'Active Depr.', value: StringUtil.toCurrency(iy.runningTotals.depreciation.activeTaxImpact) },
                  { title: '', value: StringUtil.toCurrency(iy.runningTotals.depreciation.passiveTaxImpact + iy.runningTotals.depreciation.activeTaxImpact), isTotal: true },
                  { title: '', value: undefined },
                  
                  { title: 'Equity', value: StringUtil.toCurrency(iy.runningTotals.equity) }
                ].filter(i => i)
              },

              // return
              {
                title: 'Return',
                helpHtml: 'Cumulative return',
                items: [
                  { title: 'Cashflow', value: StringUtil.toPercent((iy.runningTotals.cashOnCash.cashflow), { fractionDigits: 2 }) },
                  { title: '', value: undefined },

                  { title: 'Appreciation', value: StringUtil.toPercent((iy.runningTotals.cashOnCash.appreciation), { fractionDigits: 2 }) },
                  !property.loan ? undefined : { title: 'Paydown', value: StringUtil.toPercent((iy.runningTotals.cashOnCash.tenantPaydown), { fractionDigits: 2 }) },
                  { title: '', value: StringUtil.toPercent((iy.runningTotals.cashOnCash.appreciation) + (!property.loan ? 0.0 : iy.runningTotals.cashOnCash.tenantPaydown), { fractionDigits: 2 }), isTotal: true},
                  { title: '', value: undefined },
                  
                  { title: 'Passive Depr.', value: StringUtil.toPercent((iy.runningTotals.cashOnCash.depreciation.passive), { fractionDigits: 2 }) },
                  { title: 'Active Depr.', value: StringUtil.toPercent((iy.runningTotals.cashOnCash.depreciation.active), { fractionDigits: 2 }) },
                  { title: '', value: StringUtil.toPercent(iy.runningTotals.cashOnCash.depreciation.passive + iy.runningTotals.cashOnCash.depreciation.active, { fractionDigits: 2 }), isTotal: true },
                  { title: '', value: undefined },

                  {
                    title: 'Total',
                    value: StringUtil.toPercent(
                      (iy.runningTotals.cashOnCash.appreciation)
                      + (!property.loan ? 0.0 : iy.runningTotals.cashOnCash.tenantPaydown)
                      + (iy.runningTotals.cashOnCash.depreciation.passive),
                      { fractionDigits: 2 }
                    ),
                    helpHtml: 'Total return excluding Active Depreciation'
                  }
                ].filter(c => c)
              }
            ]
          };
        })
      }
    };
    return ret;
  }

  /**
   * 
   * @param pvm 
   * @param calendarYear 
   * @param code
   */
  investmentYearActionStart(pvm: IPageViewModel, calendarYear: number, code: string) {
    if (code == 'view-year')
      this.router.navigate(['properties', pvm.contentVm.data.code, 'years', calendarYear]);
  }

  //#region property copy form
  
  /**
   * 
   * @param vm 
   */
  private propertyCopyFormStart(vm: IContentViewModel) {
    const formVm: IPropertyCopyFormViewModel = {
      type: 'property-copy',
      title: 'Copy Property',
      
      okCancelVm: FormOkCancelComponent.okCancelVm({ okText: 'copy', cancelText: '' })    
    };
    formVm.okCancelVm.isValid = true;
    vm.formVm = formVm;
  }


  /**
   * 
   * @param vm 
   * @param fvm 
   */
  private async propertyCopyFormComplete(pvm: IPageViewModel, fvm: IPropertyDeleteFormViewModel) {
    const ret = await FormOkCancelComponent.processOkAsync(fvm.okCancelVm, 'Copying...',
      () => this.propertyService.propertyCopy(pvm.contentVm.data));
    if (ret) {
      this.formCancel(pvm);
      this.router.navigate(['']);
    }
  }


  //#endregion property copy form

  //#region disclaimer

  /**
   * 
   * @param vm 
   */
  private disclaimerFormStart(vm: IContentViewModel) {
    const formVm: IDisclaimerFormViewModel = {
      type: 'disclaimer',
      title: 'Disclaimer',
      
      okCancelVm: FormOkCancelComponent.okCancelVm({ okText: 'accept', cancelText: '' })    
    };
    formVm.okCancelVm.isValid = true;
    vm.formVm = formVm;
  }

  //#endregion disclaimer

  //#region property delete form

  /**
   * 
   * @param pvm 
   */
  private propertyDeleteFormStart(vm: IContentViewModel) {
    const formVm: IPropertyDeleteFormViewModel = {
      type: 'property-delete',
      title: 'Delete Property',

      okCancelVm: FormOkCancelComponent.okCancelVm({ okText: 'Delete' })
    };
    formVm.okCancelVm.isValid = true;
    vm.formVm = formVm;
  }

  /**
   * 
   * @param vm 
   * @param fvm 
   */
  private async propertyDeleteFormComplete(pvm: IPageViewModel, fvm: IPropertyDeleteFormViewModel) {
    const ret = await FormOkCancelComponent.processOkAsync(fvm.okCancelVm, 'Deleting...',
      () => this.propertyService.propertyDelete(pvm.contentVm.data.code));
    if (ret) {
      this.formCancel(pvm);
      this.router.navigate(['']);
    }
  }

  //#endregion property delete form

  //#region property new form

  /**
   * 
   * @param data 
   * @returns 
   */
  private propertyNewFormVm(data: IPropertyNewData): IPropertyNewFormViewModel {
    const ret: IPropertyNewFormViewModel = {
      data: data,
      type: 'property-new',
      title: 'Basic Information',

      name: FormInputComponent.viewModel(data, 'name', { title: 'Name', isRequired: true }),
      address: FormInputComponent.viewModel(data, 'address', { title: 'Address' }),
      description: FormInputComponent.viewModel(data, 'description', { title: 'Description', type: 'textarea' }),

      purchaseDate: FormInputComponent.viewModel(data, 'purchaseDate', { title: 'Purchase Date', type: 'date' }),
      purchasePrice: FormInputComponent.viewModel(data, 'purchasePrice', { title: 'Purchase Price', type: 'number-text', isRequired: true, numberOptions: { min: 1000 } }),
      marketValue: FormInputComponent.viewModel(data, 'marketValue', { title: 'Market Value', type: 'number-text', isRequired: true, numberOptions: { min: 1000 } }),

      monthlyRevenue: FormInputComponent.viewModel(data, 'monthlyRevenue', { title: 'Monthly Rent', type: 'number-text', isRequired: true, numberOptions: { min: 100 } }),
      yearlyExpenses: [
        FormInputComponent.viewModel(data.yearlyExpenses, 'taxes', { title: 'Annual Taxes', type: 'number-text', isRequired: true, numberOptions: { min: 100 } }),
        FormInputComponent.viewModel(data.yearlyExpenses, 'insurance', { title: 'Annual Insurance', type: 'number-text', isRequired: true, numberOptions: { min: 100 } })
      ],

      propertyManagement: FormInputComponent.viewModel(data, 'propertyManagement', { title: 'Mgmt. Fee %', type:'number-float', isRequired: true, numberOptions: { min: 0 }}),

      hasLoan: FormCheckboxComponent.viewModel(data, 'hasLoan', { title: 'Use a Loan' }),
      loanParams: {
        years: FormInputComponent.viewModel(data.loanParams, 'years', { title: 'Term', isRequired: true, type: 'number', numberOptions: { min: 1, max: 50 } }),
        rate: FormInputComponent.viewModel(data.loanParams, 'rate', { title: 'Interest Rate', isRequired: true, type: 'number-float', numberOptions: { min: 0 } }),
        closingCosts: FormInputComponent.viewModel(data.loanParams, 'closingCosts', { title: 'Closing Costs', isRequired: true, type: 'number-text', helpText: this.helpText_ClosingCosts(), numberOptions: { min: 0 } }),
        percentDown: FormInputComponent.viewModel(data.loanParams, 'percentDown', { title: 'Percent Down', isRequired: true, type: 'number-float', numberOptions: { min: 0 } })
      },

      okCancelVm: FormOkCancelComponent.okCancelVm({ okText: 'Create' })
    };
    this.propertyNewFormValidate(ret);
    return ret;
  }

  /**
   * 
   * @param pvm 
   * @param fvm 
   */
  private async propertyNewFormComplete(pvm: IPageViewModel, fvm: IPropertyNewFormViewModel) {
    
    let newCode: string = undefined;
    
    const ret = await FormOkCancelComponent.processOkAsync(fvm.okCancelVm, 'Saving...',
      () => this.propertyService.propertyCreate(fvm.data).pipe(
        tap((pe: IPropertyEntity) => newCode = pe.code)
      )
    );
    if (ret) {
      this.formCancel(pvm);

      if (pvm.contentVm.isNew)
        this.router.navigate(['properties', newCode], { replaceUrl: true });
      else
        this.refresher$.next({ propertyCode: pvm.contentVm.data.code, tab: 1 });
    }
  }

  /**
   * 
   * @param vm 
   * @param fvm 
   */
  propertyNewFormPurchasePriceChanged(vm: IContentViewModel, fvm: IPropertyNewFormViewModel) {
    fvm.data.marketValue = fvm.data.purchasePrice;
    fvm.data.loanParams.closingCosts = fvm.data.purchasePrice * (this.closingCostsPercent() * .01);
    this.propertyNewFormValidate(fvm);
  }

  /**
   * 
   * @param vm 
   * @param fvm
   * 
   */
  propertyNewFormChanged(vm: IContentViewModel, fvm: IPropertyNewFormViewModel) {
    this.propertyNewFormValidate(fvm);
  }

  private propertyNewFormValidate(fvm: IPropertyNewFormViewModel) {
    const issues = [
      fvm.name.validate(fvm.name), fvm.address.validate(fvm.address), fvm.description.validate(fvm.description),
      fvm.purchaseDate.validate(fvm.purchaseDate), fvm.purchasePrice.validate(fvm.purchasePrice), fvm.marketValue.validate(fvm.marketValue),
      fvm.monthlyRevenue.validate(fvm.monthlyRevenue),
      ...fvm.yearlyExpenses.map(ye => ye.validate(ye)),
      ...fvm.data.hasLoan ? [fvm.loanParams.rate, fvm.loanParams.years, fvm.loanParams.closingCosts, fvm.loanParams.percentDown].map(lp => lp.validate(lp)) : []
    ].filter(v => v.length > 0);

    fvm.okCancelVm.isValid = issues.length == 0;
  }

  //#endregion property new form

  //#region property edit form

  /**
   * 
   * @param vm 
   */
  private propertyEditFormStart(vm: IContentViewModel) {
    vm.formVm = this.propertyEditFormVm(vm);
  }

  /**
   * 
   * @param pvm 
   * @param fvm 
   */
  private async propertyEditFormComplete(pvm: IPageViewModel, fvm: IPropertyEditFormViewModel) {
    const ret = await FormOkCancelComponent.processOkAsync(fvm.okCancelVm, 'Saving...',
      () => this.propertyService.propertyUpdate(fvm.code, fvm.generalTabVm.data, fvm.loanTabVm.data.hasLoan ? fvm.loanTabVm.data.loanParams : undefined, fvm.investmentVariablesTabVm.data, fvm.data.inactive));
    if (ret) {
      this.formCancel(pvm);

      if (pvm.contentVm.isNew)
        this.router.navigate(['properties', pvm.contentVm.data.code], { replaceUrl: true });
      else
        this.refresher$.next({ propertyCode: pvm.contentVm.data.code, tab: 1 });
    }
  }

  /**
   * 
   * @param vm 
   * @returns 
   */
  private propertyEditFormVm(vm: IContentViewModel): IPropertyEditFormViewModel {
    const propertyInfo: IPropertyInfo = !vm.data ? this.propertyService.defaultPropertyInfo() : JSON.parse(JSON.stringify(vm.data.propertyInfo));
    const investmentVariables: IInvestmentVariables = !vm.data ? undefined : JSON.parse(JSON.stringify(vm.data.investmentVariables));

    const formData = {
      inactive: vm.data?.inactive
    };

    const percentEntryOptions = {
      type: 'number-float', numberOptions: { min: 0, max: 100 }, helpText: 'Percentage between 0 and 100'
    };

    const ret: IPropertyEditFormViewModel = {
      type: 'property-edit',
      title: 'Edit',
      code: vm.data.code,

      data: formData,
      inactive: FormCheckboxComponent.viewModel(formData, 'inactive', { title: 'Inactive', hideLabel: true }),

      // photos
      photosTabVm: {
        data: propertyInfo,
        title: 'Photos',

        photoFileEntryList: FormFileEntryListComponent.viewModel('Photos', propertyInfo.photoFileEntryList, () => this.propertyEditFormChanged(vm, ret), () => this.propertyService.imageCodeNew())
      },

      // general
      generalTabVm: {
        data: propertyInfo,
        title: 'General',

        name: FormInputComponent.viewModel(propertyInfo, 'name', { title: 'Name', isRequired: true }),
        address: FormInputComponent.viewModel(propertyInfo, 'address', { title: 'Address', isRequired: false }),
        description: FormInputComponent.viewModel(propertyInfo, 'description', { title: 'Description', isRequired: false }),

        purchaseDate: FormInputComponent.viewModel(propertyInfo, 'purchaseDate', { title: 'Purchase Date', isRequired: true, type: 'date' }),
        purchasePrice: FormInputComponent.viewModel(propertyInfo, 'purchasePrice', { title: 'Purchase Price', isRequired: true, type: 'number-text', numberOptions: { min: 1000 } }),
        purchaseCosts: FormInputComponent.viewModel(propertyInfo, 'purchaseCosts', { title: this.titleText_PurchaseCosts(), type: 'number-text', helpText: this.helpText_PurchaseCosts() })
      },

      // pro forma
      proFormaTabVm: {
        data: propertyInfo,
        title: 'Pro Forma',

        marketValue: FormInputComponent.viewModel(propertyInfo.initialValues, 'marketValue', { title: 'Market Value', isRequired: true, type: 'number-text', numberOptions: { min: 1000 } }),
        monthlyRevenue: FormInputComponent.viewModel(propertyInfo.initialValues, 'monthlyRevenue', { title: 'Monthly Rent', isRequired: true, type: 'number-text', numberOptions: { min: 100 } }),
        yearlyExpenses: [
          { field: 'taxes', title: 'Annual Taxes', data: propertyInfo.initialValues.yearlyExpenses, numberOptions: { min: 100 } },
          { field: 'insurance', title: 'Annual Insurance', data: propertyInfo.initialValues.yearlyExpenses, numberOptions: { min: 100 } },
          
          { field: 'hoa', title: 'Monthly HOA', data: propertyInfo.initialValues.monthlyExpenses, numberOptions: undefined },
          { field: 'grounds', title: 'Monthly Grounds', data: propertyInfo.initialValues.monthlyExpenses, numberOptions: undefined },
          { field: 'utilities', title: 'Monthly Utilities', data: propertyInfo.initialValues.monthlyExpenses, numberOptions: undefined },
          { field: 'other', title: 'Monthly Other', data: propertyInfo.initialValues.monthlyExpenses, numberOptions: undefined }
        ].map(f => FormInputComponent.viewModel(f.data, f.field, { title: f.title, isRequired: true, type: 'number-text', numberOptions: f.numberOptions }))
      },

      // loan
      loanTabVm: this.propertyEditFormLoanTabVm(vm.data?.loan?.loanParams),

      // variables
      investmentVariablesTabVm: {
        data: investmentVariables,
        title: 'Assumptions',

        fields: [
          { field: 'propertyAppreciation', title: 'Property Appreciation', data: investmentVariables, options: percentEntryOptions },
          { field: 'rentalAppreciation', title: 'Rent Appreciation', data: investmentVariables, options: percentEntryOptions  },
          { field: 'propertyManagement', title: 'Property Management', data: investmentVariables, options: percentEntryOptions  },
          { field: 'maintenance', title: 'Maintenance', data: investmentVariables, options: percentEntryOptions  },
          { field: 'vacancy', title: 'Vacancy', data: investmentVariables, options: percentEntryOptions  },
          { field: 'taxBracket', title: 'Tax Bracket', data: investmentVariables, options: percentEntryOptions  },
          { field: 'expenseInflation', title: 'Expense Inflation', data: investmentVariables, options: percentEntryOptions },
          { field: 'depreciationYears', title: 'Depreciation Years', data: investmentVariables, options: { type: 'number', isRequired: true, helpText: '27.5 or 39 years'} }
        ].map(f => FormInputComponent.viewModel(f.data, f.field, { ...f.options, title: f.title }))
      },

      okCancelVm: FormOkCancelComponent.okCancelVm({ okText: 'Save' })
    };

    this.propertyEditFormChanged(vm, ret);

    return ret;
  }

  /**
   * 
   * @param loanParams
   */
  private propertyEditFormLoanTabVm(loanParams: ILoanParams): IPropertyEditFormLoanTabViewModel {

    const loanData: { hasLoan: boolean, loanParams: ILoanParams } = {
      hasLoan: loanParams ? true : false,
      loanParams: !loanParams ? undefined : { ...loanParams }
    };

    const ret: IPropertyEditFormLoanTabViewModel = {
      title: 'Loan',

      data: loanData,

      hasLoan: FormCheckboxComponent.viewModel(loanData, 'hasLoan', { title: 'Use a Loan' }),

      fields: !loanData.loanParams ? undefined : [
        FormInputComponent.viewModel(loanData.loanParams, 'rate', { title: 'Interest Rate', isRequired: true, type: 'number-float', numberOptions: { min: 0 } }),
        FormInputComponent.viewModel(loanData.loanParams, 'years', { title: 'Years', isRequired: true, type: 'number', numberOptions: { min: 0, max: 40 } }),
        FormInputComponent.viewModel(loanData.loanParams, 'closingCosts', {
          title: 'Closing Costs', isRequired: true, type: 'number-text', helpText: this.helpText_ClosingCosts(), numberOptions: { min: 0 }
        }),
        FormInputComponent.viewModel(loanData.loanParams, 'percentDown', { title: 'Percent Down', isRequired: true, type: 'number-float', numberOptions: { min: 0 } }),
      ]
    };
    return ret;
  }

  /**
   * 
   * @param vm 
   * @param fvm 
   */
  propertyEditFormLoanCheckboxChanged(vm: IContentViewModel, fvm: IPropertyEditFormViewModel) {
    const loanParams: ILoanParams = !fvm.loanTabVm.data.hasLoan ? undefined
      : vm.data.loan ? { ...vm.data.loan.loanParams }
        : this.propertyService.defaultPropertyLoanParams();
    fvm.loanTabVm = this.propertyEditFormLoanTabVm(loanParams);
    this.propertyEditFormChanged(vm, fvm);
  }

  /**
   * 
   * @param vm 
   * @param fvm
   * 
   */
  propertyEditFormChanged(vm: IContentViewModel, fvm: IPropertyEditFormViewModel) {
    const generalTabIssues = [
      fvm.generalTabVm.name, fvm.generalTabVm.address, fvm.generalTabVm.description,
      fvm.generalTabVm.purchaseDate, fvm.generalTabVm.purchasePrice, fvm.generalTabVm.purchaseCosts
    ].map(ivm => ivm.validate(ivm).length == 0).filter(v => !v);

    const proFormaTabIssues = [
      ...fvm.proFormaTabVm.yearlyExpenses.map(ivm => ivm.validate(ivm).length == 0)
    ].filter(v => !v);

    const loanTabIssues = (
      !fvm.loanTabVm.data.hasLoan ? [] : fvm.loanTabVm.fields.map(f => f.validate(f).length == 0)
    ).filter(v => !v);

    const variableTabIssues = (
      fvm.investmentVariablesTabVm.fields.map(f => f.validate(f).length == 0)
    ).filter(v => !v);

    fvm.okCancelVm.isValid = generalTabIssues.length == 0 && loanTabIssues.length == 0 && proFormaTabIssues.length == 0 && variableTabIssues.length == 0;
  }

  //#endregion property edit form

  //#region charts

  /**
   * 
   * @returns 
   */
  private static propertyChartOptions(): any {
    const ret = {
      responsive: true,

      scales: {
        x: {
          ticks: {
          },
        },
        y: {
          ticks: {
            // Include a dollar sign in the ticks
            callback: function (value, index, ticks) {
              return StringUtil.toCurrency(value);
            }
          },
        },
      },

      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function (value: any) {
              return StringUtil.toCurrency(value.parsed.y);
            },
          },
        },
      },
    };
    return ret;
  }

  /**
    * 
    * @param title
    * @param properties
    * @param maxYears
    * @returns 
    */
  public static propertyCashflowChartVm(title: string, properties: IPropertyEntity[], maxYears: number): IPropertyChartViewModel {
    const currentYear = DateUtil.now().getFullYear();
    const years: number[] = [...Array(maxYears).keys()].map(y => currentYear + y);

    const cashflowYears = years.map(y => {
      const ret = properties.reduce((pv, cv) => {
        const iy: IInvestmentYear = cv.investmentYears.find(iy2 => iy2.calendarYear == y);
        return pv + (iy?.endOfYearTotals.estimated.cashFlow ?? 0.0);
      }, 0);
      return ret;
    });

    const ret: IPropertyChartViewModel = {
      title: title,
      bodyText: `Forecasted annual cashflow of ${StringUtil.toCurrency(cashflowYears[cashflowYears.length-1])} for year ${maxYears}`,
      helpHtml: 'Annual cashflow',

      chartType: 'line',
      chartDatasets: [
        {
          data: cashflowYears,
          label: title
        }
      ],

      chartLabels: years.map(y => y.toString()),

      chartColors: [
        {
          backgroundColor: ['#98cf4d'],
          hoverBackgroundColor: ['#3f924a'],
          borderWidth: 2,
        }
      ],

      chartOptions: PropertyPageComponent.propertyChartOptions()
    };
    return ret;
  }

  /**
     * @param title
     * @param properties
     * @param maxYears
     * @returns 
     */
  public static propertyEquityChartVm(title: string, properties: IPropertyEntity[], maxYears: number): IPropertyChartViewModel {
    const currentYear = DateUtil.now().getFullYear();
    const years: number[] = [...Array(maxYears).keys()].map(y => currentYear + y);

    const equityYears = years.map(y => {
      const totalEquity = properties.reduce((pv, cv) => {
        const iy: IInvestmentYear = cv.investmentYears.find(iy2 => iy2.calendarYear == y);
        return pv + (iy?.runningTotals.equity ?? 0.0);
      }, 0);
      return totalEquity;
    });

    const ret: IPropertyChartViewModel = {
      title: title,
      bodyText: `Forecasted equity of ${StringUtil.toCurrency(equityYears[equityYears.length-1])} for year ${maxYears}`,
      helpHtml: 'Year over year equity (appreciation and debt paydown) growth',

      chartType: 'line',
      chartDatasets: [
        {
          data: equityYears,
          label: title
        }
      ],

      chartLabels: years.map(y => y.toString()),

      chartColors: [
        {
          backgroundColor: ['#98cf4d'],
          hoverBackgroundColor: ['#3f924a'],
          borderWidth: 2,
        }
      ],

      chartOptions: PropertyPageComponent.propertyChartOptions()
    };
    return ret;
  }

  //#endregion charts

}

/**
 * 
 */
interface IPageViewModel extends IPageLoaderViewModel {
  contentVm?: IContentViewModel;
  pageHeaderVm?: IPageHeaderViewModel;
}

/**
 * 
 */
interface IContentViewModel {
  isNew?: boolean;
  data: IPropertyEntity;

  formVm?: IFormViewModel;

  chartVms: IPropertyChartViewModel[];

  infoVm: IPropertyInfoViewModel;
  investmentYearsVm: IInvestmentYearsViewModel;
}

/**
 * 
 */
interface IPropertyInfoViewModel {
  pictureUrl: string;
  panelVms: IPagePanelViewModel[];
}

/**
 * 
 */
interface IPagePanelViewModel {
  title: string;
  helpHtml?: string;
  actions?: { code: string, title: string, tooltip: string }[];

  badgeVm?: IBadgeViewModel;

  sections: {
    title: string;

    items: { title: string, value: string, helpHtml?: string }[];
  }[];
}

/**
 * 
 */
interface IInvestmentYearsViewModel {
  title: string;  

  tableVm: {
    headers: string[],
    rows: {
      title: string;
      helpHtml?: string;
      calendarYear: number;
      actions?: { code: string, title: string }[];
      cells: {
        title?: string;
        helpHtml?: string;
        items: {
          title?: string;
          value: string;
          isTotal?: boolean;
        }[];
      }[];
    }[];
  };
}

/**
 * 
 */
interface IPropertyNewFormViewModel extends IFormViewModel {
  data: IPropertyNewData;

  name: IFormInputViewModel;
  description: IFormInputViewModel;
  address: IFormInputViewModel;

  purchaseDate: IFormInputViewModel;
  purchasePrice: IFormInputViewModel;
  marketValue: IFormInputViewModel;

  monthlyRevenue: IFormInputViewModel;
  yearlyExpenses: IFormInputViewModel[];

  propertyManagement: IFormInputViewModel;

  hasLoan: IFormCheckboxViewModel;
  loanParams: {
    years: IFormInputViewModel;
    rate: IFormInputViewModel;
    closingCosts: IFormInputViewModel;
    percentDown: IFormInputViewModel
  };
}

/**
 * 
 */
interface IPropertyDeleteFormViewModel extends IFormViewModel {

}

/**
 * 
 */
interface IDisclaimerFormViewModel extends IFormViewModel {
}

/**
 * 
 */
interface IPropertyCopyFormViewModel extends IFormViewModel {

}

/**
 * 
 */
interface IPropertyEditFormViewModel extends IFormViewModel {
  code: string;

  data: {
    inactive: boolean
  };

  inactive: IFormCheckboxViewModel;

  photosTabVm: {
    data: IPropertyInfo;
    title: string;

    photoFileEntryList: IFormFileEntryListViewModel;
  };

  generalTabVm: {
    data: IPropertyInfo;

    title: string;

    name: IFormInputViewModel;
    address: IFormInputViewModel;
    description: IFormInputViewModel;

    purchaseDate: IFormInputViewModel;
    purchasePrice: IFormInputViewModel;
    purchaseCosts: IFormInputViewModel;
  };

  proFormaTabVm: {
    data: IPropertyInfo;

    title: string;

    marketValue: IFormInputViewModel;
    monthlyRevenue: IFormInputViewModel;
    yearlyExpenses: IFormInputViewModel[];
  };

  loanTabVm: IPropertyEditFormLoanTabViewModel;

  investmentVariablesTabVm: {
    data: IInvestmentVariables;
    title: string;

    fields: IFormInputViewModel[];
  }
}

/**
 * 
 */
interface IPropertyEditFormLoanTabViewModel {
  title: string;

  data: {
    hasLoan: boolean;
    loanParams?: ILoanParams;
  };

  hasLoan: IFormCheckboxViewModel;

  fields: IFormInputViewModel[];
}

/**
 * 
 */
interface IPropertyLoanFormViewModel extends IFormViewModel {
  title: string;

  monthlyPayment: string;

  payments: {
    title: string;

    principal: string;
    interest: string;

    start: string;
    end: string;
  }[];
}

/**
 * 
 */
export interface IPropertyChartViewModel {
  title: string;
  bodyText: string;
  helpHtml?: string;

  chartType: string;
  chartDatasets: any[];
  chartLabels: any[];
  chartColors?: any[];
  chartOptions: any;
}